import React, { useMemo } from "react";
import { Box } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { findLandingThemeById, LandingTheme } from "../landing-page/landingThemes";
import { LandingThemeProvider, useLandingTheme } from "../landing-page/LandingThemeContext";
import { LandingLanguageProvider } from "../landing-page/LandingLanguageContext";
import { isMarketingSiteOrigin, resolveAssignedLandingVariant } from "../landing-page/handoff/appHandoff";
import LandingNav from "../landing-page/sections/LandingNav";
import LandingFooter from "../landing-page/sections/LandingFooter";
import { MarketingPageLanguage, MarketingPageTemplate } from "../../marketing-content/marketingContentLoader";

// Same query param as the landing page: `?v=moss` previews a page in a given landing theme.
const THEME_QUERY_PARAM = "v";
// Off the marketing domain (local dev, the app's own origin) every page renders in this theme.
const DEFAULT_MARKETING_THEME_ID = "clearing";

function resolveMarketingPageTheme(explicitThemeId: string | null): LandingTheme {
  if (explicitThemeId) return findLandingThemeById(explicitThemeId);
  // A visitor on jungleai.com keeps the landing variant they were assigned, so every sub-page looks
  // like the home page they arrived through.
  if (isMarketingSiteOrigin()) return findLandingThemeById(resolveAssignedLandingVariant());
  return findLandingThemeById(DEFAULT_MARKETING_THEME_ID);
}

/**
 * The custom properties marketingContent.css reads. They are set on the `.marketing-page` element
 * (see MarketingPageMain) so the static HTML's `:root` defaults are overridden by the active theme
 * once React renders.
 */
export function buildMarketingPageCssVariables(theme: LandingTheme): React.CSSProperties {
  return {
    "--marketing-display-font": theme.font.display,
    "--marketing-body-font": theme.font.body,
    "--marketing-heading-color": theme.color.heading,
    "--marketing-text-color": theme.color.text,
    "--marketing-muted-color": theme.color.muted,
    "--marketing-accent-color": theme.color.accent,
    "--marketing-border-color": theme.color.border,
    "--marketing-surface-color": theme.color.surface,
    "--marketing-page-background": theme.color.pageBg,
  } as React.CSSProperties;
}

/**
 * The `<main class="marketing-page marketing-page--{template}">` element of the shared markup
 * contract (see the spec and scripts/generate-marketing-static-html.mjs), carrying the theme's CSS
 * custom properties. Every template page renders its content inside one of these.
 */
export const MarketingPageMain = React.memo(function MarketingPageMain({
  template,
  children,
}: {
  template: MarketingPageTemplate;
  children: React.ReactNode;
}) {
  const theme = useLandingTheme();
  const marketingPageCssVariables = useMemo(() => buildMarketingPageCssVariables(theme), [theme]);
  return (
    <main className={`marketing-page marketing-page--${template}`} style={marketingPageCssVariables}>
      {children}
    </main>
  );
});

/**
 * Everything in a marketing sub-page's body: the theme, page background, nav and footer.
 * Each page registers its complete head through MarketingDocumentHead, including its fonts and
 * document language. The page's head also reports its page view after setting the title.
 */
export const MarketingSiteShell = React.memo(function MarketingSiteShell({
  lang,
  children,
}: {
  lang: MarketingPageLanguage;
  children: React.ReactNode;
}) {
  const [searchParams] = useSearchParams();
  const explicitThemeId = searchParams.get(THEME_QUERY_PARAM);
  const theme = useMemo(() => resolveMarketingPageTheme(explicitThemeId), [explicitThemeId]);
  // As on the landing page, a light theme whose nav sits on a dark surface (Moss) gets that
  // surface painted behind the nav.
  const navBackground = theme.navOnDark && !theme.isDark ? theme.color.accentDark : undefined;

  return (
    <LandingThemeProvider theme={theme}>
      <Box data-marketing-page bg={theme.color.pageBg} color={theme.color.text} fontFamily={theme.font.body} minH="100vh" sx={theme.pageBackgroundLayer}>
        {/* The template pages reuse landing sections (upload controls, testimonials, closing call to
            action), which read their copy from this provider. */}
        <LandingLanguageProvider language={lang}>
          <Box bg={navBackground}>
            {/* The language choice is in the footer on every marketing page, as on the home page. */}
            <LandingNav language={lang} showLanguageSelector={false} />
          </Box>
          {children}
          <LandingFooter variant="themed" language={lang} showLanguageSelector />
        </LandingLanguageProvider>
      </Box>
    </LandingThemeProvider>
  );
});
